import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { submitEntry, fileToBase64 } from '../../../services/faceOfBelvoirService';

const BANNER_IMAGE = 'https://BelvoirCastlePull.b-cdn.net/Website/Belvoir%20Castle%20Christmas-94.webp';
const ENTRIES_CLOSED = false;

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 3rem 2rem 5rem;
  animation: ${fadeUp} 0.5s ease;

  @media (max-width: 768px) {
    padding: 2rem 1.25rem 4rem;
  }
`;

// ---------------------------------------------------------------------------
// Hero copy section
// ---------------------------------------------------------------------------

const HeroSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem 3.5rem;
  margin-bottom: 2.5rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.07);
  border-top: 4px solid ${props => props.theme.colors.accent};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 220px;
    height: 220px;
    background: radial-gradient(circle at top right, rgba(157, 109, 73, 0.06) 0%, transparent 70%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 2rem 1.75rem;
  }
`;

const Eyebrow = styled.p`
  margin: 0 0 0.75rem;
  font-family: ${props => props.theme.fonts.body};
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.accent};
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.8rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;
  line-height: 1.15;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const TitleDivider = styled.div`
  width: 56px;
  height: 3px;
  background: ${props => props.theme.colors.accent};
  border-radius: 2px;
  margin: 1rem 0 1.5rem;
`;

const Intro = styled.p`
  font-family: ${props => props.theme.fonts.body};
  font-size: 1.1rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.75;
  margin: 0 0 1rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

// ---------------------------------------------------------------------------
// Prize box
// ---------------------------------------------------------------------------

const PrizeBox = styled.div`
  background: linear-gradient(135deg, rgba(16, 29, 64, 0.04) 0%, rgba(157, 109, 73, 0.06) 100%);
  border: 2px solid ${props => props.theme.colors.accent};
  border-radius: 14px;
  padding: 1.75rem 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;
  }
`;

const PrizeTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PrizeText = styled.p`
  font-family: ${props => props.theme.fonts.body};
  font-size: 1rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.7;
  margin: 0;
`;

const TermsHeading = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.35rem;
  color: ${props => props.theme.colors.primary};
  margin: 2rem 0 0.75rem;
`;

const TermsList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  font-family: ${props => props.theme.fonts.body};
  font-size: 1rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.7;

  li + li {
    margin-top: 0.35rem;
  }
`;

const ConfirmLine = styled.p`
  font-family: ${props => props.theme.fonts.body};
  font-size: 1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  line-height: 1.6;
  margin: 1.5rem 0 0;
`;

// ---------------------------------------------------------------------------
// Form section
// ---------------------------------------------------------------------------

const FormCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2.5rem 3rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.07);

  @media (max-width: 768px) {
    padding: 1.75rem 1.5rem;
  }
`;

const FormTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.75rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;
`;

const FormSubtitle = styled.p`
  font-family: ${props => props.theme.fonts.body};
  font-size: 0.95rem;
  color: ${props => props.theme.colors.secondary};
  margin: 0 0 2rem;
`;

const FieldGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-family: ${props => props.theme.fonts.body};
  font-weight: 700;
  font-size: 0.95rem;
  color: ${props => props.theme.colors.primary};
`;

const Required = styled.span`
  color: ${props => props.theme.colors.accent};
  margin-left: 3px;
`;

const FieldHint = styled.p`
  margin: 0.3rem 0 0;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.5;
`;

const inputStyles = props => `
  width: 100%;
  box-sizing: border-box;
  padding: 0.8rem 1rem;
  border: 2px solid rgba(16, 29, 64, 0.12);
  border-radius: 10px;
  font-family: ${props.theme.fonts.body};
  font-size: 0.97rem;
  color: ${props.theme.colors.primary};
  background: white;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  outline: none;

  &::placeholder {
    color: rgba(16, 29, 64, 0.35);
  }

  &:focus {
    border-color: ${props.theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.12);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const Input = styled.input`${inputStyles}`;
const Textarea = styled.textarea`
  ${inputStyles}
  min-height: 110px;
  resize: vertical;
  line-height: 1.6;
`;

// ---------------------------------------------------------------------------
// Photo upload
// ---------------------------------------------------------------------------

const PhotoUploadArea = styled.label`
  display: block;
  border: 2px dashed ${props => props.hasFile ? props.theme.colors.accent : 'rgba(16, 29, 64, 0.2)'};
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  background: ${props => props.hasFile ? 'rgba(157, 109, 73, 0.04)' : 'rgba(16, 29, 64, 0.02)'};
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    border-color: ${props => props.theme.colors.accent};
    background: rgba(157, 109, 73, 0.04);
  }

  input {
    display: none;
  }
`;

const PhotoPreview = styled.img`
  max-width: 100%;
  max-height: 240px;
  border-radius: 10px;
  object-fit: cover;
  display: block;
  margin: 0 auto 0.75rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
`;

const PhotoIcon = styled.div`
  margin-bottom: 0.75rem;
  color: rgba(16, 29, 64, 0.35);
`;

const PhotoLabel = styled.p`
  font-family: ${props => props.theme.fonts.body};
  font-size: 0.95rem;
  color: ${props => props.theme.colors.secondary};
  margin: 0;
`;

const PhotoFilename = styled.p`
  font-family: ${props => props.theme.fonts.body};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  margin: 0.5rem 0 0;
`;

// ---------------------------------------------------------------------------
// Consent checkbox
// ---------------------------------------------------------------------------

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  padding: 1.25rem 1.5rem;
  border: 2px solid rgba(16, 29, 64, 0.1);
  border-radius: 12px;
  background: rgba(16, 29, 64, 0.02);
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.accent};
    background: rgba(157, 109, 73, 0.04);
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 2px;
  accent-color: ${props => props.theme.colors.accent};
  cursor: pointer;
`;

const CheckboxText = styled.span`
  font-family: ${props => props.theme.fonts.body};
  font-size: 0.95rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.55;
`;

// ---------------------------------------------------------------------------
// Submit button / feedback
// ---------------------------------------------------------------------------

const SubmitButton = styled.button`
  width: 100%;
  padding: 1.1rem 2rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 12px;
  font-family: ${props => props.theme.fonts.body};
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.25s ease;
  margin-top: 2rem;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(16, 29, 64, 0.2);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div`
  margin-top: 1.25rem;
  padding: 1rem 1.25rem;
  border-radius: 10px;
  font-family: ${props => props.theme.fonts.body};
  font-size: 0.95rem;
  font-weight: 600;
  background: ${props => props.type === 'success' ? '#d4edda' : props.type === 'error' ? '#f8d7da' : '#d1ecf1'};
  color: ${props => props.type === 'success' ? '#155724' : props.type === 'error' ? '#721c24' : '#0c5460'};
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : props.type === 'error' ? '#f5c6cb' : '#bee5eb'};
  line-height: 1.6;
`;

const SuccessCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.07);
  animation: ${fadeUp} 0.5s ease;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const SuccessIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;

  svg {
    display: block;
  }
`;

const SuccessTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
`;

const SuccessText = styled.p`
  font-family: ${props => props.theme.fonts.body};
  font-size: 1.05rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.7;
  max-width: 520px;
  margin: 0 auto;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(16, 29, 64, 0.1);
  margin: 2rem 0;
`;

const ClosedBanner = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, #1a2d5c 100%);
  color: white;
  border-radius: 14px;
  padding: 1.5rem 2rem;
  margin-bottom: 2.5rem;
  text-align: center;
  box-shadow: 0 4px 20px rgba(16, 29, 64, 0.18);

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;
  }
`;

const ClosedBannerTitle = styled.p`
  margin: 0 0 0.5rem;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: 0.02em;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const ClosedBannerText = styled.p`
  margin: 0;
  font-family: ${props => props.theme.fonts.body};
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.88);
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FaceOfBelvoir = () => {
  const blockPickerRef = useRef(false);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Be the Face of Belvoir | Christmas Family Photoshoot';
    return () => {
      document.title = previous;
    };
  }, []);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    familyInfo: '',
    availability: '',
    termsAccepted: false,
    marketingConsent: false,
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePhotoChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = ev => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);

    // Some browsers fire a follow-up click on the label when the dialog closes.
    blockPickerRef.current = true;
    window.setTimeout(() => {
      blockPickerRef.current = false;
    }, 400);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!form.termsAccepted) {
      setError('Please agree to the terms and confirm your family is available on Wednesday 11th November 2026.');
      return;
    }

    if (!photoFile) {
      setError('Please attach a family photo before submitting.');
      return;
    }

    setSubmitting(true);
    try {
      const photoData = await fileToBase64(photoFile);
      await submitEntry({
        ...form,
        photoData,
        photoFileName: photoFile.name,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <ParallaxBanner
        image={BANNER_IMAGE}
        title="Be the Face of Belvoir"
        subtitle="Christmas Family Photoshoot"
      />

      <ContentContainer>
        {ENTRIES_CLOSED && (
          <ClosedBanner>
            <ClosedBannerTitle>New entries now closed</ClosedBannerTitle>
            <ClosedBannerText>
              Thank you to everyone who registered for the Christmas family photoshoot.
              We are no longer accepting new registrations.
            </ClosedBannerText>
          </ClosedBanner>
        )}

        {submitted ? (
          <SuccessCard>
            <SuccessIcon>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </SuccessIcon>
            <SuccessTitle>Registration Received</SuccessTitle>
            <SuccessText>
              Thank you for registering for the Christmas family photoshoot at Belvoir Castle
              on Wednesday 11th November 2026. We've received your details and will be in touch
              to confirm your session.
            </SuccessText>
            <StatusMessage type="success" style={{ marginTop: '2rem', textAlign: 'left' }}>
              A confirmation email has been sent to <strong>{form.email}</strong>.
            </StatusMessage>
          </SuccessCard>
        ) : (
          <>
            {/* Hero copy */}
            <HeroSection>
              <Eyebrow>Christmas Photoshoot</Eyebrow>
              <PageTitle>Be the Face of Belvoir</PageTitle>
              <TitleDivider />
              <Intro>
                Are you free on Wednesday 11th November? Join us for a Christmas family photoshoot
                around Belvoir Castle, before the festive season officially begins.
              </Intro>
              <Intro style={{ marginBottom: 0 }}>
                Enjoy a sneak peek of the castle’s Christmas installation before it opens to the
                public, and meet Father Christmas too! It’s the perfect chance to wander through
                the festive scenes with your family while we capture the magic.
              </Intro>

              <PrizeBox>
                <PrizeTitle>A little about how this works</PrizeTitle>
                <PrizeText>
                  This is a complimentary shoot, and in return, the photographs taken will be used
                  in our marketing materials. By booking a session, you agree that images from the
                  day, including those featuring you and your family, may be used across our website,
                  social media channels, printed brochures, advertising and press promotion.
                </PrizeText>
              </PrizeBox>

              <TermsHeading>Terms and conditions</TermsHeading>
              <TermsList>
                <li>Photographs may be used for marketing purposes by Belvoir Castle.</li>
                <li>No payment or fee is due for the use of your image.</li>
                <li>All images remain the copyright of Belvoir Castle.</li>
                <li>Every adult attending must agree to these terms, and a parent or guardian gives consent on behalf of any children in their party.</li>
                <li>You will receive digital images from your session for personal use.</li>
              </TermsList>
              <ConfirmLine>
                By completing this form you are confirming your availability for Wednesday 11th November 2026.
              </ConfirmLine>
            </HeroSection>

            {/* Form */}
            {!ENTRIES_CLOSED && (
            <FormCard>
              <FormTitle>Register Your Family</FormTitle>
              <FormSubtitle>
                Fill in your details and upload a family photograph. We’ll be in touch to confirm your session.
              </FormSubtitle>

              <form onSubmit={handleSubmit} noValidate>
                {/* Name */}
                <FieldGroup>
                  <Label htmlFor="fob-name">
                    Your Name <Required>*</Required>
                  </Label>
                  <Input
                    id="fob-name"
                    name="name"
                    type="text"
                    placeholder="Full name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                </FieldGroup>

                {/* Email */}
                <FieldGroup>
                  <Label htmlFor="fob-email">
                    Your Email Address <Required>*</Required>
                  </Label>
                  <Input
                    id="fob-email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                </FieldGroup>

                {/* Phone */}
                <FieldGroup>
                  <Label htmlFor="fob-phone">
                    Your Contact Number <Required>*</Required>
                  </Label>
                  <Input
                    id="fob-phone"
                    name="phone"
                    type="tel"
                    placeholder="e.g. 07700 900 123"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                  />
                </FieldGroup>

                {/* Address */}
                <FieldGroup>
                  <Label htmlFor="fob-address">
                    Your Address <Required>*</Required>
                  </Label>
                  <Textarea
                    id="fob-address"
                    name="address"
                    placeholder="House number, street, town, postcode"
                    value={form.address}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                    style={{ minHeight: '90px' }}
                  />
                </FieldGroup>

                <Divider />

                {/* Family info */}
                <FieldGroup>
                  <Label htmlFor="fob-family">
                    About Your Family <Required>*</Required>
                  </Label>
                  <FieldHint>
                    How many members of your family are there? Please include any children's ages.
                  </FieldHint>
                  <Textarea
                    id="fob-family"
                    name="familyInfo"
                    placeholder="e.g. 2 adults, 3 children (ages 4, 7 and 10)"
                    value={form.familyInfo}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                    style={{ marginTop: '0.5rem' }}
                  />
                </FieldGroup>

                {/* Availability */}
                <FieldGroup>
                  <Label htmlFor="fob-availability">
                    Availability for Wednesday 11th November <Required>*</Required>
                  </Label>
                  <FieldHint>
                    The photoshoot takes place on Wednesday 11th November 2026. Please confirm
                    your family is available, and note any constraints we should be aware of.
                  </FieldHint>
                  <Textarea
                    id="fob-availability"
                    name="availability"
                    placeholder="e.g. Yes, we're available on Wednesday 11th November."
                    value={form.availability}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                    style={{ marginTop: '0.5rem' }}
                  />
                </FieldGroup>

                <Divider />

                {/* Photo upload */}
                <FieldGroup>
                  <Label>
                    Family Photo <Required>*</Required>
                  </Label>
                  <FieldHint style={{ marginBottom: '0.75rem' }}>
                    Please attach a recent photo of your whole family. Accepted formats: JPG, PNG,
                    WEBP (max 10MB).
                  </FieldHint>
                  <PhotoUploadArea
                    hasFile={!!photoFile}
                    onClick={e => {
                      if (blockPickerRef.current) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  >
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handlePhotoChange}
                      disabled={submitting}
                    />
                    {photoPreview ? (
                      <>
                        <PhotoPreview src={photoPreview} alt="Family photo preview" />
                        <PhotoFilename>{photoFile?.name}</PhotoFilename>
                        <PhotoLabel style={{ marginTop: '0.5rem', fontSize: '0.82rem' }}>
                          Click to change photo
                        </PhotoLabel>
                      </>
                    ) : (
                      <>
                        <PhotoIcon>
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                          </svg>
                        </PhotoIcon>
                        <PhotoLabel>
                          <strong>Click to upload</strong> your family photo
                        </PhotoLabel>
                        <PhotoLabel style={{ fontSize: '0.82rem', marginTop: '0.35rem' }}>
                          JPG, PNG or WEBP — max 10MB
                        </PhotoLabel>
                      </>
                    )}
                  </PhotoUploadArea>
                </FieldGroup>

                <Divider />

                <FieldGroup>
                  <CheckboxRow>
                    <Checkbox
                      type="checkbox"
                      name="termsAccepted"
                      checked={form.termsAccepted}
                      onChange={handleChange}
                      disabled={submitting}
                      required
                    />
                    <CheckboxText>
                      I agree to the terms and conditions above. Every adult attending agrees, and I
                      give consent as a parent or guardian for any children in our party. I confirm
                      we are available on Wednesday 11th November 2026. <Required>*</Required>
                    </CheckboxText>
                  </CheckboxRow>
                </FieldGroup>

                {/* Marketing consent */}
                <FieldGroup style={{ marginBottom: '0.5rem' }}>
                  <CheckboxRow>
                    <Checkbox
                      type="checkbox"
                      name="marketingConsent"
                      checked={form.marketingConsent}
                      onChange={handleChange}
                      disabled={submitting}
                    />
                    <CheckboxText>
                      I'm happy to receive marketing emails from Belvoir Castle about upcoming events,
                      offers, and news. You can unsubscribe at any time.
                    </CheckboxText>
                  </CheckboxRow>
                </FieldGroup>

                {error && (
                  <StatusMessage type="error">{error}</StatusMessage>
                )}

                <SubmitButton type="submit" disabled={submitting}>
                  {submitting ? 'Submitting your registration…' : 'Register for the Photoshoot'}
                </SubmitButton>

                <FieldHint style={{ textAlign: 'center', marginTop: '1rem' }}>
                  By registering, you confirm your availability for Wednesday 11th November 2026
                  and agree that photographs from the day may be used in Belvoir Castle marketing.
                  Your details will not be shared with third parties.
                </FieldHint>
              </form>
            </FormCard>
            )}
          </>
        )}
      </ContentContainer>

      <Footer />
    </PageContainer>
  );
};

export default FaceOfBelvoir;
